import React from "react";
import {
  useDispatchWrapper,
  usePoliciesByCustomer,
} from "../../hooks";
import { useFormik } from "formik";
import {
  Button,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../components/formsComponents";
import { CustomerAutoSearch } from "../../components/AutoCompleteSearches/CustomerAutoSearch";
import {
  CustomDatePicker,
} from "../../components/formsComponents/CustomDatePicker";
import { FileUpload } from "../../features/FileUpload/components/FileUpload";


import { InsurerCodeAutoSearch } from "../../components/AutoCompleteSearches";


import { RightPanel } from "../../components/RightPanel";


import { ICSIOEDocs } from "../../redux/csioEdocs/csioEdocs.types";
import { upsertCsioEdocsAsync } from "../../redux/csioEdocs/csioEdocsActions";
import { upsertNoticeAndEdocAsync } from "../../redux/notices/noticesActions";
import { ICustomerFullDetails } from "../../redux/customer/customer.types";
import {
  Business_Purpose_type,
  Business_Types,
  LINE_OF_BUSINESS,
} from "../../constants/constants";
import { UsersAutoSearchByRole } from "../../components/AutoCompleteSearches/UsersAutoSearchByRole";

export interface ManageCSIOEdocsProps {
  open: boolean;
  data: ICSIOEDocs;
  onComplete: () => void;
  onClose: () => void;
  customerId?: string;
  customerPolicyId?: string;
  type?: "guid" | "code";
  useUnifiedUpsert?: boolean;
}

export const ManageEdocsRightPanel: React.FC<ManageCSIOEdocsProps> = (
  props
) => {
  const {
    open,
    onClose,
    onComplete,
    data,
    customerId,
    customerPolicyId,
    type = "guid",
    useUnifiedUpsert,
  } = props;
  const [file, setFile] = React.useState<File | null>(null);
  const [saveLoading, setSaveLoading] = React.useState(false);

  const dispatch = useDispatchWrapper();
  const isUpdate = data.notice_and_edoc_code ? true : false;
  const { policiesList, policiesLoading, fetchPoliciesByCustomerId } =
    usePoliciesByCustomer();
  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: {
      ...data,
      customer_id: customerId as any,
      customer_policy_id: customerPolicyId as any,
    },
    validate: (values) => {
      const errors: any = {};
      const errorMessage = "This field is required.";
      if (!values.policy_number) {
        errors.policy_number = errorMessage;
      } else if (!values.customer_id) {
        errors.customer_id = errorMessage;
      } else if (!values.type) {
        errors.type = errorMessage;
      } else if (!values.business_purpose_type) {
        errors.business_purpose_type = errorMessage;
      }
      return errors;
    },
    onSubmit: async (values) => {
      setSaveLoading(true);
      const handleResult = (isSuccess: boolean) => {
        if (isSuccess) {
          onComplete();
        }
        setSaveLoading(false);
      };
      if (useUnifiedUpsert) {
        dispatch(
          upsertNoticeAndEdocAsync(
            { ...values } as any,
            { ...data } as any,
            "EDOC",
            data.notice_and_edoc_code ? true : false,
            file,
            handleResult
          )
        );
      } else {
        dispatch(
          upsertCsioEdocsAsync({ ...values }, file, handleResult)
        );
      }
    },
  });

  const handleCustomer = (data: ICustomerFullDetails) => {
    if (data.customer_id) {
      setValues({
        ...values,
        customer_id: data.customer_id as number,
        customer_name: data.customer_name,
        customer_policy_id: null,
        policy_number: null,
      });
    }
  };

  // React.useEffect(() => {
  //   if (data) {
  //     setValues(data);
  //   }
  // }, []);

    React.useEffect(() => {
      if (customerPolicyId && policiesList.length > 0) {
        const policy = policiesList.find(
          (x) => x.customer_policy_id === Number(customerPolicyId)
        );
        console.log("policy", policy)
        if (policy) {
          setValues((prevValues) => ({
            ...prevValues,
            customer_policy_id: customerPolicyId,
            policy_number: policy.policy_number,
            contract_term_effective_date: policy.effective_date,
            contract_term_expiry_date: policy.expiry_date,
            insurance_company: policy.insurer,
            line_of_business: policy.line_of_business,
            branch_code: policy.branch_code,
            producer_one_id: policy.producer_one,
            producer_code: policy.producer_one_code,
            customer_id: policy.customer_id,
            customer_name: policy.customer_name,
          }));
        }
      } else {
        if (isUpdate) {
          setValues(data);
        }
      }
    }, [policiesList, data]);

  const handlePolicyChange = (e: SelectChangeEvent<unknown>) => {
    const policyId = e.target.value as string;
    const policy = policiesList.find(
      (x) => x.customer_policy_id === Number(policyId)
    );
    if (policy) {
      setValues({
        ...values,
        customer_policy_id: policyId,
        policy_number: policy.policy_number,
        contract_term_effective_date: policy.effective_date,
        contract_term_expiry_date: policy.expiry_date,
        insurance_company: policy.insurer,
        line_of_business: policy.line_of_business,
        branch_code: policy.branch_code,
        producer_one_id: policy.producer_one,
        producer_code: policy.producer_one_code,
        customer_id: policy.customer_id,
      });
    }
  };

  React.useEffect(() => {
    if (values.customer_id) {
      fetchPoliciesByCustomerId(values.customer_id as number);
    }
  }, [values.customer_id]);

  return (
    <RightPanel
      open={open}
      heading="Create/Edit"
      onClose={onClose}
      width="80%"
      isWrappedWithForm
      onFormSubmit={handleSubmit}
      actionButtons={() => {
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <Button variant="contained" type="submit" fullWidth>
                Save
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" fullWidth onClick={onClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        );
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} md={3}>
          <CustomerAutoSearch
            label="Customer"
            value={values.customer_id}
            onSelect={handleCustomer}
            error={!!errors.customer_id}
            // helperText={
            //   errors.customer_id as string ||
            //   (customerId && customerPolicyId ? "Auto-filled from policy context" : "")
            // }
            disabled={!!(customerId && customerPolicyId)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Policy</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="customer_policy_id"
            value={Number(values.customer_policy_id)}
            onChange={handlePolicyChange}
            placeholder="Select One"
            options={policiesList.map((item) => {
              return {
                label: item.policy_number,
                value: item.customer_policy_id,
              };
            })}
            // helperText={
            //   errors.customer_policy_id as string ||
            //   (customerId && customerPolicyId ? "Auto-filled from policy context" : "")
            // }
            disabled={!!(customerId && customerPolicyId)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Type</CustomFormLabel>
          <ControlledCustomSelect
            name="type"
            value={values.type}
            onChange={handleChange}
            displayEmpty
            options={Business_Types.map((type) => ({
              label: type,
              value: type,
            }))}
            fullWidth
            helperText={errors.type as string}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Financed</CustomFormLabel>
          <ControlledCustomSelect
            name="financed"
            fullWidth
            value={values.financed}
            onChange={handleChange}
            variant="outlined"
            options={[
              { label: "Yes", value: "Y" },
              { label: "No", value: "N" },
            ]}
            displayEmpty
            placeholder="Select One"
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Financed By</CustomFormLabel>
          <CustomTextField
            name="financed_by"
            fullWidth
            value={values.financed_by}
            onChange={handleChange}
            type="text"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <CustomFormLabel>Business Purpose Type</CustomFormLabel>
          <ControlledCustomSelect
            name="business_purpose_type"
            value={values.business_purpose_type}
            onChange={handleChange}
            displayEmpty
            options={
              values.type && Business_Purpose_type[values.type]
                ? Business_Purpose_type[values.type]
                : []
            }
            fullWidth
            helperText={errors.business_purpose_type as string}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Insurer</CustomFormLabel>
          <InsurerCodeAutoSearch
            value={values.insurance_company}
            fullWidth
            onSelect={(newValue) => {
              setValues({
                ...values,
                insurance_company: newValue.value,
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Effective Date</CustomFormLabel>
          <CustomDatePicker
            value={values.contract_term_effective_date || ""}
            onChange={(value) =>
              setFieldValue("contract_term_effective_date", value)
            }
            fullWidth
            errorMessage={errors.contract_term_effective_date as string}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <CustomFormLabel>Expiry Date</CustomFormLabel>
          <CustomDatePicker
            value={values.contract_term_expiry_date || ""}
            onChange={(value) =>
              setFieldValue("contract_term_expiry_date", value)
            }
            fullWidth
            errorMessage={errors.contract_term_expiry_date as string}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <CustomFormLabel>Line of Business</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            name="line_of_business"
            fullWidth
            value={values.line_of_business}
            onChange={handleChange}
            displayEmpty
            placeholder={"Select one"}
            options={LINE_OF_BUSINESS}
            helperText={errors.line_of_business as string}
          ></ControlledCustomSelect>
        </Grid>
        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Action Needed</CustomFormLabel>
          <ControlledCustomSelect
            name="action_needed"
            fullWidth
            value={values.action_needed}
            onChange={handleChange}
            variant="outlined"
            options={[
              { label: "Invoice", value: "Invoice" },
              {
                label: "Invoice & Email Docs-Customer",
                value: "Invoice & Email Docs-Customer",
              },
              {
                label: "Invoice & Email Docs-Producer",
                value: "Invoice & Email Docs-Producer",
              },
              {
                label: "Invoice & Email Docs - Customer & Producer",
                value: "Invoice & Email Docs - Customer & Producer",
              },
            ]}
            displayEmpty
            placeholder="Select One"
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Action Status</CustomFormLabel>
          <ControlledCustomSelect
            name="action_status"
            fullWidth
            value={values.action_status}
            onChange={handleChange}
            variant="outlined"
            options={[
              { label: "Pending", value: "PENDING" },
              {
                label: "Completed",
                value: "COMPLETED",
              },
            ]}
            displayEmpty
            placeholder="Select One"
          />
        </Grid>
                <Grid item xs={12} md={3}>
           <UsersAutoSearchByRole
                          label="Producer Code"
                          value={values.producer_one_id}
                          onSelect={(newValue) => {
                            setValues({
                              ...values,
                              producer_one_id: newValue.user_fact_id as any,
                              producer_code: newValue.user_code,
                            });
                          }}
                        />
                        </Grid>
                        <Grid item xs={9}></Grid>
        <Grid item xs={12} lg={6}>
          <Typography variant="h5" fontWeight={800}>
            Attachments EDOCS
          </Typography>
          <FileUpload
            value={values.attachment}
            // disabled
            onChange={(file) => setFile(file)}
            deleteDisabled={isUpdate}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <CustomFormLabel>Remarks</CustomFormLabel>
          <CustomTextField
            multiline
            rows={3}
            name="remarks"
            value={values.remarks}
            onChange={handleChange}
            fullWidth
            error={!!errors.remarks}
            helperText={errors.remarks as string}
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <CustomFormLabel>Status</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={values.status}
            name="status"
            onChange={handleChange}
            placeholder="Select one"
            displayEmpty
            options={[
              { label: "Open", value: "Open" },
              { label: "Closed", value: "Closed" },
            ]}
          />
        </Grid>

     
        {/* <Grid item xs={12} lg={6}></Grid>
              {values.attachment && (
                <Grid item xs={12} lg={6}>
                  <CustomFormLabel>Attachment</CustomFormLabel>
                  <FileUpload value={values.attachment} deleteDisabled />
                </Grid>
              )} */}
      </Grid>
    </RightPanel>
  );
};
